import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2, AlertCircle, RefreshCw, Search, ChevronRight, Award, Users,
  MapPin, Building2, AlertTriangle, MessagesSquare,
} from "lucide-react";
import {
  useAdminGetChapterEventRequestsQuery,
  type ChapterEventRequest,
  type ChapterEventStatus,
} from "../../chapter/api/chapterEventsApi";
import ChapterEventStatusBadge from "../../chapter/components/ChapterEventStatusBadge";
import { AppendageChip } from "../../chapter/components/ChapterEventAppendage";

/**
 * The secretariat's tray of chapter CPD events.
 *
 * Two of these statuses are work: `pending` wants a decision on the points, and
 * `attendance_submitted` wants the allocation that creates the event. The others
 * are here to be looked up, not acted on — which is why the server sorts the
 * list by status with those two first rather than by date.
 *
 * The badges on the tabs come from the server's own counts, so they count the
 * whole tray rather than whatever page happens to be loaded.
 */

function fmtDate(iso?: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
  });
}

// `countKey` is a status, not a loose string — the server's counts are keyed by
// status, and typing it as `string` is what made indexing them an implicit any.
// The "All" tab has no count of its own, hence the optional.
const TABS: { key: string; label: string; countKey?: ChapterEventStatus }[] = [
  { key: "attendance_submitted", label: "Awaiting allocation", countKey: "attendance_submitted" },
  { key: "pending", label: "Awaiting approval", countKey: "pending" },
  // After pending, not before it: these are with the CHAPTER, not with the
  // reviewer. Listed so a query nobody ever answered can be found again, which
  // is the one way a request can quietly go nowhere.
  { key: "queried", label: "With the chapter", countKey: "queried" },
  { key: "approved", label: "Approved", countKey: "approved" },
  { key: "allocated", label: "Allocated", countKey: "allocated" },
  { key: "rejected", label: "Rejected", countKey: "rejected" },
  { key: "", label: "All" },
];

export default function AdminChapterEventRequestsPage() {
  // Opens on the queue that needs the most work: allocation is the step where a
  // chapter is waiting on an event to exist and its members on their points.
  const [tab, setTab] = useState("attendance_submitted");
  const [search, setSearch] = useState("");

  const { data, isLoading, isFetching, error, refetch } = useAdminGetChapterEventRequestsQuery({
    ...(tab ? { status: tab } : {}),
    ...(search.trim() ? { search: search.trim() } : {}),
  });

  const rows: ChapterEventRequest[] = data?.data ?? [];
  // Partial, so the empty fallback is a legal value of the same type rather than
  // a second type the union has to be widened across.
  const counts: Partial<Record<ChapterEventStatus, number>> = data?.meta?.counts ?? {};

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 bg-white rounded-lg shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Chapter Event Requests</h2>
          <p className="text-xs text-gray-500 font-medium mt-1 max-w-2xl">
            Events a chapter has held and wants CPD for. Approve the points first;
            once the chapter has uploaded its attendance, allocating creates the
            chapter event and credits every member on the register.
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="p-2.5 rounded-lg border text-gray-500 hover:bg-gray-50 disabled:opacity-40 self-start"
          title="Refresh"
        >
          <RefreshCw className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} />
        </button>
      </div>

      {!!error && (
        <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{(error as any)?.data?.message || "Could not load the requests."}</span>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex gap-2 overflow-x-auto pb-1 flex-1">
          {TABS.map((t) => {
            const count = t.countKey ? counts[t.countKey] ?? 0 : 0;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wide whitespace-nowrap border transition-colors ${
                  tab === t.key
                    ? "bg-gray-900 text-white border-gray-900"
                    : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"
                }`}
              >
                {t.label}
                {!!t.countKey && count > 0 && (
                  <span
                    className={`px-1.5 rounded-full text-[10px] ${
                      tab === t.key ? "bg-white/20" : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Title, venue or chapter…"
            className="border pl-9 pr-3 py-2 rounded-lg text-sm outline-none focus:border-blue-500 w-full sm:w-64"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="py-16 flex flex-col items-center gap-2 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span className="text-xs font-medium">Loading requests…</span>
        </div>
      ) : rows.length === 0 ? (
        <div className="py-16 text-center space-y-1 border-2 border-dashed border-gray-200 rounded-2xl">
          <p className="text-sm font-bold text-gray-500">Nothing here</p>
          <p className="text-xs text-gray-400">No chapter event request matches this view.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.id} className="border border-gray-200 rounded-2xl p-4 hover:border-gray-900 transition-colors">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="min-w-0 space-y-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-bold text-gray-900 break-words">{r.title}</span>
                    <ChapterEventStatusBadge status={r.status} />
                    {/* Only on the ones still to be decided: it says whether
                        there is a programme on file to decide the points by. */}
                    {r.can_review && <AppendageChip complete={r.appendage_complete} />}
                    {/* Per reviewer, not per desk: one reviewer opening a
                        request does not mark it read for the others. */}
                    {!!r.unread_count && r.unread_count > 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wide bg-amber-400 text-amber-950">
                        <MessagesSquare className="w-3 h-3" />
                        {r.unread_count} new
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="inline-flex items-center gap-1 font-semibold text-gray-700">
                      <Building2 className="w-3 h-3" /> {r.chapter?.name ?? "—"}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {r.location}
                    </span>
                    <span>{fmtDate(r.start_date)}</span>
                  </div>
                  <div className="text-xs text-gray-500 flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="inline-flex items-center gap-1">
                      <Award className="w-3 h-3" />
                      {r.approved_cpd_points === null ? (
                        <>
                          <span className="font-bold text-gray-900">{r.proposed_cpd_points} pts</span>
                          <span className="text-gray-400">requested</span>
                        </>
                      ) : r.cpd_was_reduced ? (
                        <>
                          <span className="line-through text-gray-400">{r.proposed_cpd_points}</span>
                          <span className="font-bold text-gray-900">{r.effective_cpd_points} pts</span>
                          <span className="text-amber-600 font-semibold">(reduced)</span>
                        </>
                      ) : (
                        <span className="font-bold text-gray-900">{r.effective_cpd_points} pts</span>
                      )}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {r.attendees_count} on register
                      {/* The figure that matters at allocation: how many of them
                          can actually be credited. */}
                      {r.attendees_count > 0 && (
                        <span className="text-gray-400">
                          ({r.member_attendees_count} member{r.member_attendees_count === 1 ? "" : "s"})
                        </span>
                      )}
                    </span>
                    {r.unmatched_count > 0 && (
                      // Visible from the tray, so the secretariat can see which
                      // registers are still waiting on a chapter without opening
                      // each one.
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300 text-[10px] font-bold uppercase tracking-wide">
                        <AlertTriangle className="w-3 h-3" /> {r.unmatched_count} unmatched
                      </span>
                    )}
                    {!!r.requested_by && <span className="text-gray-400">by {r.requested_by}</span>}
                  </div>
                </div>

                <Link
                  to={`/admin/chapter-event-requests/${r.id}`}
                  className={`inline-flex items-center gap-1 px-3 py-2 rounded-lg text-[11px] font-bold uppercase tracking-wide shrink-0 ${
                    r.can_allocate || r.can_review
                      ? "bg-gray-900 text-white hover:bg-gray-800"
                      : "border text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {r.can_allocate ? "Allocate CPD" : r.can_review ? "Review" : "Open"}
                  <ChevronRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
